import { useCallback, useEffect, useState } from 'react';

import { internalRequest } from '@/utils/internalRequest';

import type { UseUrl, UseUrlProps } from './types';

export function useUrl<ResponseBody = unknown>({
  url,
  pause,
  initial,
}: UseUrlProps<ResponseBody>): UseUrl<ResponseBody> {
  const [isLoading, setIsLoading] = useState<boolean>(!pause); // is loading by default when not paused
  const [data, setData] = useState<ResponseBody | null>(initial || null);
  const [error, setError] = useState<string>();

  const handleFetch = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await internalRequest<ResponseBody>({
        url,
        method: 'GET',
      });
      if (result) {
        setData(result);
      }
      setIsLoading(false);
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unknown error occurred');
      }
      setIsLoading(false);
    }
  }, [url]);

  useEffect(() => {
    if (pause) return; // still paused
    handleFetch();
  }, [pause, handleFetch]);

  return {
    data,
    error,
    isLoading,
    refetch: handleFetch,
  };
}
